import React, { StrictMode, type ReactNode } from 'react';
import {
	EntryPointContainer,
	type EntryPoint,
	type EntryPointComponent,
	type PreloadedEntryPoint,
} from 'react-relay';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { MAIN_CONTENT } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';

const EMPTY_PROPS = {};
type RootComponent = EntryPointComponent<{}, {}, {}, {}>;
type RootEntryPoint = EntryPoint<RootComponent, {}>;

/** Loads a consumer whose root already owns SPA lifecycle or legacy non-SPA state handling. */
export const RootLifecycleEntryPointContainer = ({
	route,
	fallback = null,
}: {
	route: Route;
	fallback?: ReactNode;
}): React.JSX.Element | null => {
	const entryPointReference = usePageLayoutEntryPoint<RootEntryPoint>(MAIN_CONTENT);
	const isPageHydrating = useIsHydrateRenderMode();
	const Skeleton = route.skeleton;

	if (!entryPointReference) return null;

	return (
		<EntryPointReferencePlaceholder
			entryPointReference={entryPointReference}
			fallback={Skeleton ? <Skeleton /> : fallback}
			name="route_entry_point"
			isSSRPlaceholderEnabled={
				!isPageHydrating ? route.entryPointOptions?.isSSRPlaceholderEnabled : undefined
			}
		>
			<EntryPointContainer<PreloadedEntryPoint<RootComponent>>
				entryPointReference={entryPointReference}
				props={EMPTY_PROPS}
			/>
		</EntryPointReferencePlaceholder>
	);
};

/** Retains SpaRouteComponent's Strict Mode policy and loading fallback for migrated wrappers. */
export const LegacyComponentEntryPointContainer = ({
	route,
}: {
	route: Route;
}): React.JSX.Element => {
	const content = <RootLifecycleEntryPointContainer route={route} fallback={<Spinner />} />;
	if ((route.isStrictModeEnabled ?? true) && fg('jfp-jira-incremental-strict-mode')) {
		return <StrictMode>{content}</StrictMode>;
	}
	return content;
};
